import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { User, LoginForm, RegisterForm, AuthContextType, Company } from '../types';
import apiService from '../services/api';
import { useQuery } from '@tanstack/react-query';
import Modal from '../components/ui/Modal';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const warningTimerRef = useRef<number | null>(null);
  const expiryTimerRef = useRef<number | null>(null);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [originalSystemAdminToken, setOriginalSystemAdminToken] = useState<string | null>(null);

  // Get company settings for session configuration
  const { data: company } = useQuery<Company>({
    queryKey: ['company'],
    queryFn: async () => {
      const response = await apiService.getCompany();
      return response.success ? response.data : null;
    },
    // Re-enabled with better error handling
    enabled: !!token && (typeof window === 'undefined' || localStorage.getItem('isSystemAdmin') !== 'true'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on failure
  });

  useEffect(() => {
    // Check for existing token and user on app load
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const isSystemAdmin = localStorage.getItem('isSystemAdmin') === 'true';
    const storedImpersonation = localStorage.getItem('impersonation');
    const storedOriginalToken = localStorage.getItem('originalSystemAdminToken');

    console.log('AuthContext useEffect:', {
      hasStoredToken: !!storedToken,
      hasStoredUser: !!storedUser,
      isSystemAdmin,
      storedToken: storedToken ? 'present' : 'missing',
      hasImpersonation: !!storedImpersonation
    });

    if (storedToken) {
      setToken(storedToken);
      
      // Check if we're in impersonation mode
      if (storedImpersonation && storedOriginalToken) {
        try {
          const impersonationData = JSON.parse(storedImpersonation);
          setIsImpersonating(true);
          setImpersonatedUser(impersonationData.impersonatedUser);
          setOriginalSystemAdminToken(storedOriginalToken);
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          }
        } catch (e) {
          console.error('Error parsing impersonation data:', e);
        }
      } else if (storedUser) {
        setUser(JSON.parse(storedUser));
      } else if (isSystemAdmin) {
        // For system admin, create a minimal user object
        const systemAdminUser = {
          id: 'systemadmin',
          username: 'systemadmin',
          email: 'superadmin@workorder.com',
          firstName: 'System',
          lastName: 'Admin',
          companyId: 0, // System admins are not tied to a company
          roleId: 0, // System admins don't have a role ID
          roleName: 'SystemAdmin',
          createdDate: new Date().toISOString(),
          isActive: true
        };
        console.log('Setting system admin user:', systemAdminUser);
        setUser(systemAdminUser);
        // Also store it in localStorage for consistency
        localStorage.setItem('user', JSON.stringify(systemAdminUser));
      }
    }
    
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const syncProfilePhoto = async () => {
      if (!token || (typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true')) {
        return;
      }

      try {
        const response = await apiService.getUserProfile();
        if (!response.success || !response.data) {
          return;
        }

        const profilePhotoUrl = response.data.profilePhotoUrl as string | undefined;
        setUser((prev) => {
          if (!prev || prev.profilePhotoUrl === profilePhotoUrl) {
            return prev;
          }

          const updatedUser = { ...prev, profilePhotoUrl };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          return updatedUser;
        });
      } catch {
        // Non-critical: header falls back to initials if sync fails
      }
    };

    syncProfilePhoto();
  }, [token]);

  // Decode JWT exp
  const tokenExp = useMemo(() => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      if (payload && typeof payload.exp === 'number') {
        return payload.exp * 1000; // ms
      }
    } catch {}
    return null;
  }, [token]);

  const clearTimers = () => {
    if (warningTimerRef.current) window.clearTimeout(warningTimerRef.current);
    if (expiryTimerRef.current) window.clearTimeout(expiryTimerRef.current);
    warningTimerRef.current = null;
    expiryTimerRef.current = null;
  };

  const scheduleSessionTimers = (explicitExpiryMs?: number | null) => {
    clearTimers();
    
    // Skip session timers for system admins
    const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';
    if (isSystemAdmin) {
      console.log('Skipping session timers for system admin');
      return;
    }
    
    const expSource = explicitExpiryMs ?? tokenExp;
    if (!expSource) return;
    
    const now = Date.now();
    
    // Use company settings for warning lead time, fallback to 30 seconds
    const warningLeadSeconds = company?.sessionWarningLeadSeconds ?? 30;
    const warnAt = Math.max(0, expSource - (warningLeadSeconds * 1000) - now);
    const expireIn = Math.max(0, expSource - now);
    
    warningTimerRef.current = window.setTimeout(() => setShowExpiryWarning(true), warnAt);
    expiryTimerRef.current = window.setTimeout(() => {
      setShowExpiryWarning(false);
      logout();
    }, expireIn);
  };

  useEffect(() => {
    scheduleSessionTimers();
    return clearTimers;
  }, [tokenExp, company?.sessionWarningLeadSeconds]);

  const refresh = async () => {
    if (isRefreshing) return false;
    setIsRefreshing(true);
    if (!token) return false;
    try {
      const response = await apiService.refreshToken();
      if (response.success && response.data?.token && response.data?.user) {
        const { token: newToken, user: newUser } = response.data;
        setToken(newToken);
        setUser(newUser);
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(newUser));
        setShowExpiryWarning(false);
        // Prefer server-provided expiry if present
        const expiresAtStr = (response.data as any)?.expiresAt || (response.data as any)?.ExpiresAt;
        const newExpMs = expiresAtStr ? Date.parse(expiresAtStr) : null;
        scheduleSessionTimers(newExpMs);
        return true;
      }
    } catch {}
    finally { setIsRefreshing(false); }
    return false;
  };

  const login = async (credentials: LoginForm) => {
    try {
      setIsLoading(true);
      const response = await apiService.login(credentials);
      
      if (response.success) {
        const { token: newToken, user: newUser, refreshToken } = response.data;
        
        setToken(newToken);
        setUser(newUser);
        
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(newUser));
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
        
        // Clear system admin flag for regular company users
        localStorage.removeItem('isSystemAdmin');
      } else {
        const errorMessage = response.message || 'Login failed';
        console.error('Login failed:', response);
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      setIsLoading(false);
      // Log the full error for debugging
      console.error('Login error:', error);
      
      // Handle network errors specifically
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || !error.response) {
        const apiUrl = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:5269/api`;
        console.error('Network error - API URL:', apiUrl);
        throw new Error(`Unable to connect to the server. Please ensure the backend is running at ${apiUrl}`);
      }
      
      const errorMessage = error?.response?.data?.message || error?.message || 'Login failed. Please try again.';
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterForm) => {
    try {
      setIsLoading(true);
      const response = await apiService.register(data);
      
      if (response.success) {
        const { token: newToken, user: newUser, refreshToken } = response.data;
        
        setToken(newToken);
        setUser(newUser);
        
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(newUser));
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      setIsLoading(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    clearTimers();
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('isSystemAdmin');
  };

  const updateUserProfilePhoto = (profilePhotoUrl: string) => {
    if (user) {
      const updatedUser = { ...user, profilePhotoUrl };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const startImpersonation = (impersonationToken: string, impersonatedUserData: User) => {
    // Store the original system admin token
    const currentToken = token || localStorage.getItem('token');
    if (currentToken) {
      setOriginalSystemAdminToken(currentToken);
      localStorage.setItem('originalSystemAdminToken', currentToken);
    }

    // Set impersonation token and user
    setToken(impersonationToken);
    setUser(impersonatedUserData);
    setIsImpersonating(true);
    setImpersonatedUser(impersonatedUserData);

    localStorage.setItem('token', impersonationToken);
    localStorage.setItem('user', JSON.stringify(impersonatedUserData));
    localStorage.setItem('impersonation', JSON.stringify({
      impersonatedUser: impersonatedUserData,
      startedAt: new Date().toISOString()
    }));

    // Clear system admin flag during impersonation
    localStorage.removeItem('isSystemAdmin');
  };

  const endImpersonation = async () => {
    try {
      // Get original token before making any changes
      const originalToken = originalSystemAdminToken || localStorage.getItem('originalSystemAdminToken');
      
      if (!originalToken) {
        // If no original token, clear everything and redirect to login
        console.error('No original system admin token found');
        logout();
        window.location.replace('/systemadmin/login');
        return;
      }

      // Restore original system admin session to localStorage FIRST
      // This ensures the redirect will use the correct token
      localStorage.setItem('token', originalToken);
      localStorage.setItem('isSystemAdmin', 'true');
      // Set default light theme for SystemAdmin
      localStorage.setItem('theme', 'light');
      localStorage.setItem('theme:systemadmin', 'light');

      // Restore system admin user to localStorage
      const systemAdminUser = {
        id: 'systemadmin',
        username: 'systemadmin',
        email: 'superadmin@workorder.com',
        firstName: 'System',
        lastName: 'Admin',
        companyId: 0,
        roleId: 0,
        roleName: 'SystemAdmin',
        createdDate: new Date().toISOString(),
        isActive: true
      };
      localStorage.setItem('user', JSON.stringify(systemAdminUser));

      // Clear impersonation data from localStorage
      localStorage.removeItem('impersonation');
      localStorage.removeItem('originalSystemAdminToken');

      // Update React state
      setToken(originalToken);
      setUser(systemAdminUser);
      setIsImpersonating(false);
      setImpersonatedUser(null);
      setOriginalSystemAdminToken(null);

      // Try to call backend to log impersonation end (fire and forget)
      apiService.endImpersonation().catch(error => {
        console.warn('Failed to log impersonation end to backend (non-critical):', error);
      });

      // Force a full page reload to the system admin page
      // Use window.location.href to ensure a full page reload (not just React Router navigation)
      window.location.href = '/systemadmin';
    } catch (error) {
      console.error('Error ending impersonation:', error);
      // On error, clear everything and redirect to system admin login
      logout();
      window.location.replace('/systemadmin/login');
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!token && !!user,
    isLoading,
    login,
    logout,
    register,
    refresh,
    updateUserProfilePhoto,
    isImpersonating,
    impersonatedUser,
    startImpersonation,
    endImpersonation,
  };

  // Debug logging
  console.log('AuthContext Debug:', {
    hasUser: !!user,
    hasToken: !!token,
    isAuthenticated: !!token && !!user,
    isLoading,
    userData: user,
    tokenData: token ? 'present' : 'missing'
  });

  return (
    <AuthContext.Provider value={value}>
      {children}
      <Modal
        isOpen={showExpiryWarning}
        onClose={() => {}} // Prevent closing by clicking outside
        title="Session expiring soon"
        maxWidth="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Your session will expire in about {Math.ceil((company?.sessionWarningLeadSeconds ?? 30) / 60)} minute{(company?.sessionWarningLeadSeconds ?? 30) >= 60 ? 's' : ''}. Stay signed in?
          </p>
          <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
            <button className="btn-secondary px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600" onClick={logout}>Sign out</button>
            <button className="btn-primary px-3 py-2 bg-accent-color text-white rounded disabled:opacity-60" disabled={isRefreshing} onClick={async () => { const ok = await refresh(); if (!ok) logout(); }}>{isRefreshing ? 'Refreshing…' : 'Stay signed in'}</button>
          </div>
        </div>
      </Modal>
    </AuthContext.Provider>
  );
};
